import { Layout, Menu, ConfigProvider } from "antd";

import "./Sidebar.css";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, Search } from "lucide-react";
import ProfilePopup from "./Sidebar/ProfilePopup.tsx";
import { useIsDashmoteStaff } from "../hooks/useIsDashmoteStaff";
import { useAdminStatus } from "../features/dashai/hooks/useAdminStatus";
import { SIDEBAR_WIDTH } from "../layout";
import { activeEntryFor, railGroups } from "../navigation";

const { Sider } = Layout;

// Persisted expand/collapse state, following the admin:excludeDashmote
// precedent (AdminShell.tsx). Stores the collapsed group keys, so the default
// (no entry) is every group expanded.
const NAV_COLLAPSE_KEY = "admin:navCollapsed";
const groupKey = (name) => `group-${name}`;

const readCollapsedGroups = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(NAV_COLLAPSE_KEY));
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
};

const DLogo = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="10"
    height="12"
    viewBox="0 0 10 12"
    fill="none"
  >
    <circle
      cx="5.27523"
      cy="1.42513"
      r="0.925135"
      stroke="white"
    />
    <path
      d="M3.8501 1.42517L0.999828 1.42517"
      stroke="white"
      strokeLinecap="round"
    />
    <path
      d="M8.57471 4.63684C9.08554 4.63684 9.50031 5.05086 9.50049 5.56165C9.50049 6.07258 9.08565 6.48743 8.57471 6.48743C8.06392 6.48725 7.6499 6.07247 7.6499 5.56165C7.65008 5.05097 8.06403 4.63702 8.57471 4.63684Z"
      stroke="white"
    />
    <path
      d="M7.30805 5.56201L1.44932 5.56201"
      stroke="white"
      strokeLinecap="round"
    />
    <path
      d="M6.88721 8.7738C7.39804 8.7738 7.81281 9.18782 7.81299 9.69861C7.81299 10.2095 7.39815 10.6244 6.88721 10.6244C6.37642 10.6242 5.9624 10.2094 5.9624 9.69861C5.96258 9.18793 6.37653 8.77398 6.88721 8.7738Z"
      stroke="white"
    />
    <path
      d="M5.4624 9.69897H1.187"
      stroke="white"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * `page` only when the entry *is* the open page; `true` when it is the active
 * ancestor of one. Announcing "current page" on a parent would name a page the
 * user is not on.
 */
const ariaCurrent = (entry, selectedKey, pathname) => {
  if (entry.route !== selectedKey) return undefined;
  return entry.route === pathname ? "page" : "true";
};

const toMenuItem = (entry, selectedKey, pathname) => ({
  key: entry.route,
  // Fixed 24px slot so labels line up regardless of glyph.
  icon: (
    <span className="w-6 h-10 flex items-center justify-center">
      <entry.icon className="h-5 w-5" />
    </span>
  ),
  label: entry.label,
  // Ant marks the active item with a class, which is invisible to assistive
  // tech. rc-menu spreads unrecognised props onto the rendered element, so this
  // reaches the DOM; Sidebar.test.jsx asserts it still does.
  "aria-current": ariaCurrent(entry, selectedKey, pathname),
});

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAdminStatus();

  // Display only, and shared with `UserManagementProtectedRoute` so the rail and
  // the route guard cannot disagree. Neither is the authorization boundary — the
  // server is.
  const isStaff = useIsDashmoteStaff();

  // Derived, not stored: the previous copy mirrored the path into state through
  // an effect and resolved it with a hardcoded if-chain, which silently
  // highlighted nothing on any page the chain had not been taught about.
  const activeEntry = activeEntryFor(location.pathname);

  const groups = railGroups({ isStaff, isAdmin });

  // Every page is its own rail row, so the open page's own route is the
  // selected key.
  const selectedKey = activeEntry?.route ?? "";

  // Collapsed is the stored source of truth; open state is derived each render,
  // so groups render correctly once `isStaff` resolves rather than being frozen
  // at first paint.
  const [collapsedGroups, setCollapsedGroups] = useState(readCollapsedGroups);

  // The active item's group always renders open, so the user can see where they
  // are. A collapse of that group is still remembered — it takes effect once
  // they navigate away.
  const activeGroupKey = activeEntry?.group ? groupKey(activeEntry.group) : null;
  const isGroupOpen = (key) => key === activeGroupKey || !collapsedGroups.includes(key);

  const toggleGroup = (key) => {
    const next = collapsedGroups.includes(key)
      ? collapsedGroups.filter((k) => k !== key)
      : [...collapsedGroups, key];
    setCollapsedGroups(next);
    localStorage.setItem(NAV_COLLAPSE_KEY, JSON.stringify(next));
  };

  const groupHeaderLabel = (name, open) => (
    <button
      type="button"
      className="admin-nav-group-toggle"
      aria-expanded={open}
      onClick={() => toggleGroup(groupKey(name))}
    >
      <span>{name}</span>
      <ChevronDown
        className={`admin-nav-chevron${open ? " admin-nav-chevron--open" : ""}`}
        aria-hidden="true"
      />
    </button>
  );

  // A null-group block, when the manifest declares one, pins at the top while
  // the grouped sections scroll beneath it, so the rail never overflows the
  // viewport. Every entry is grouped today, so this is empty.
  const pinnedItems = groups
    .filter((group) => group.name === null)
    .flatMap((group) => group.entries.map((entry) => toMenuItem(entry, selectedKey, location.pathname)));

  const scrollableItems = groups
    .filter((group) => group.name !== null)
    .map((group) => {
      const key = groupKey(group.name);
      const open = isGroupOpen(key);
      return {
        type: "group",
        key,
        label: groupHeaderLabel(group.name, open),
        children: open
          ? group.entries.map((entry) => toMenuItem(entry, selectedKey, location.pathname))
          : [],
      };
    });

  const handleMenuClick = ({ key }) => {
    // Group headers aren't navigable.
    if (!key || !key.startsWith("/")) return;
    if (location.pathname === key) return;
    navigate(key);
  };

  const menuProps = {
    theme: "dark",
    mode: "inline",
    selectedKeys: [selectedKey],
    onClick: handleMenuClick,
    className: "custom-sidebar-menu",
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "Inter",
        },
        components: {
          Input: {
            fontFamily: "Inter",
          },
        },
      }}
    >
      <div className="fixed left-0 top-0 h-screen z-50">
        <Sider
          className="h-full overflow-hidden"
          style={{
            boxShadow: "4px 0 10px rgba(0, 0, 0, 0.1)",
            backgroundColor: "#0e49b5",
          }}
          width={SIDEBAR_WIDTH}
          trigger={null}
        >
          <div className="flex flex-col h-full">
            {/* Logo — also the home link to the landing page. */}
            <div
              className="flex items-center h-[72px] shrink-0"
              style={{ paddingLeft: "18px" }}
            >
              <button
                type="button"
                onClick={() => navigate("/")}
                aria-label="Go to admin home"
                className="flex items-end border-0 bg-transparent p-0 text-white font-[Satoshi] font-bold text-[2rem] leading-[2.5rem] tracking-[-0.32px] cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
              >
                <span className="relative">
                  D
                  <div className="absolute top-[14px] left-[6px]">
                    <DLogo />
                  </div>
                </span>
                <span className="text-white font-[Satoshi] font-bold text-[2rem] leading-[2.5rem] tracking-[-0.32px]">
                  ashONE
                </span>
              </button>
            </div>

            {/* Opens the ⌘K command palette (AdminCommandPalette). Kept as a
                visible affordance because the shortcut alone is undiscoverable.
                Decoupled via a window event so the rail owns no palette state. */}
            <div className="px-3 pb-2 shrink-0">
              <button
                type="button"
                className="admin-nav-search"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent("admin:open-command-palette"))
                }
              >
                <Search
                  className="admin-nav-search__icon"
                  aria-hidden="true"
                />
                <span className="admin-nav-search__label">Search</span>
                <kbd className="admin-nav-search__kbd">⌘K</kbd>
              </button>
            </div>

            {/* Ant's Menu exposes role="menu"/"menuitem" — the application-menu
                pattern, not site navigation. The landmark is what lets a screen
                reader user jump to the nav and hear it announced as such. */}
            <nav
              aria-label="Admin navigation"
              className="flex flex-col min-h-0 flex-grow"
            >
              {pinnedItems.length > 0 && (
                <div className="shrink-0">
                  <Menu
                    {...menuProps}
                    items={pinnedItems}
                  />
                </div>
              )}

              <div className="flex-grow min-h-0 overflow-y-auto admin-sidebar-scroll">
                <Menu
                  {...menuProps}
                  items={scrollableItems}
                />
              </div>
            </nav>

            {/* User Profile */}
            <div className="px-3 py-4 shrink-0">
              <ProfilePopup isExpanded />
            </div>
          </div>
        </Sider>
      </div>
    </ConfigProvider>
  );
};

export default Sidebar;
